"use client";

import { useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  Button,
  useDisclosure,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  ModalFooter,
  Text,
  HStack,
  Tooltip,
  IconButton,
  Select,
  Textarea,
  Image,
  Box,
  useToast,
  InputGroup,
  InputLeftElement,
  Tag,
  TagLabel,
  TagCloseButton,
  Flex,
} from "@chakra-ui/react";
import { DeleteIcon, SearchIcon, ViewIcon } from "@chakra-ui/icons";
import {
  fetchAdminNotificationsAsync,
  selectAdminNotifications,
  selectAdminNotificationsLastPage, // Imported this
  addNotificationsAsync,
  fetchUsersAsync,
  selectUsers,
  clearUsers,
} from "../../Notifications/NotificationsSlice";
import { useDispatch, useSelector } from "react-redux";

function NotificationList() {
  // Modal disclosures
  const {
    isOpen: isAddModalOpen,
    onOpen: onOpenAddModal,
    onClose: onCloseAddModal,
  } = useDisclosure();

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onOpenDeleteModal,
    onClose: onCloseDeleteModal,
  } = useDisclosure();

  const {
    isOpen: isViewModalOpen,
    onOpen: onOpenViewModal,
    onClose: onCloseViewModal,
  } = useDisclosure();

  // State management
  const [newNotification, setNewNotification] = useState({
    send_type: "notification",
    send_to: "",
    title: "",
    description: "",
  });

  // Image Upload States
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const notifications = useSelector(selectAdminNotifications);
  const totalPages = useSelector(selectAdminNotificationsLastPage); // Get API provided last page

  const [deletingNotification, setDeletingNotification] = useState(null);
  const [viewingNotification, setViewingNotification] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  const toast = useToast();
  const dispatch = useDispatch();

  const searchResults = useSelector(selectUsers);

  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  // Fetch notifications whenever the current page changes
  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchAdminNotificationsAsync(currentPage))
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, [dispatch, currentPage]);

  // Handle input changes for text fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewNotification({
      ...newNotification,
      [name]: value,
    });

    if (name === "send_to") {
      setSelectedRecipients([]);
      setSearchQuery("");
      dispatch(clearUsers());
    }
  };

  // Handle Image File Selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Basic validation for image types
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file type",
          description: "Please upload an image file.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setSelectedImageFile(file);
      setPreviewImageUrl(URL.createObjectURL(file));
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 2) {
      let user_type = null;
      if (newNotification.send_to === "Selected candidate") {
        user_type = "candidate";
      } else if (newNotification.send_to === "Selected Employer") {
        user_type = "employer";
      }

      dispatch(
        fetchUsersAsync({
          keyword: query,
          user_type: user_type,
        })
      );
    }
  };

  const addRecipient = (user) => {
    const userId = user.uuid || user.id;
    if (!selectedRecipients.some((r) => (r.uuid || r.id) === userId)) {
      setSelectedRecipients([...selectedRecipients, user]);
    }
    setSearchQuery("");
  };

  const removeRecipient = (recipientId) => {
    setSelectedRecipients(
      selectedRecipients.filter((r) => (r.uuid || r.id) !== recipientId)
    );
  };

  // Save new notification using FormData
  const handleSave = () => {
    if (!newNotification.title || !newNotification.send_to) {
      setError("Title and recipient are required");
      return;
    }

    if (
      (newNotification.send_to === "Selected candidate" ||
        newNotification.send_to === "Selected Employer") &&
      selectedRecipients.length === 0
    ) {
      setError("Please select at least one recipient");
      return;
    }

    // Format send_to based on selection
    let formattedSendTo;
    if (newNotification.send_to === "All candidate") {
      formattedSendTo = "all_candidate";
    } else if (newNotification.send_to === "All Employer") {
      formattedSendTo = "all_employer";
    } else if (newNotification.send_to === "Selected candidate") {
      formattedSendTo = "selected_candidate";
    } else if (newNotification.send_to === "Selected Employer") {
      formattedSendTo = "selected_employer";
    } else {
      formattedSendTo = newNotification.send_to.toLowerCase().replace(" ", "_");
    }

    // Create FormData object
    const formData = new FormData();
    formData.append("send_type", newNotification.send_type);
    formData.append("send_to", formattedSendTo);
    formData.append("title", newNotification.title);
    formData.append("description", newNotification.description);

    // Append Image if exists
    if (selectedImageFile) {
      formData.append("image", selectedImageFile);
    }

    // Append recipients
    if (
      newNotification.send_to === "Selected candidate" ||
      newNotification.send_to === "Selected Employer"
    ) {
      // When sending arrays via FormData to many backends (like PHP/Laravel),
      // you often need to append with []
      selectedRecipients.forEach((recipient) => {
        formData.append("recipients[]", recipient.uuid || recipient.id);
      });
    }

    // Dispatch the addNotificationsAsync action with FormData
    dispatch(addNotificationsAsync(formData))
      .then((res) => {
        if (res.error) {
          throw new Error(res.payload || "Failed to create");
        }

        // Reset form and close modal
        resetForm();
        onCloseAddModal();

        toast({
          title: "Notification created",
          status: "success",
          duration: 3000,
          isClosable: true,
        })

        // Refresh the notifications list (reset to page 1 to see new item)
        setCurrentPage(1);
        dispatch(fetchAdminNotificationsAsync(1));
      })
      .catch((error) => {
        setError(
          "Failed to create notification: " + (error.message || "Unknown error")
        );
      });
  };

  const openDeleteModal = (notification) => {
    setDeletingNotification(notification);
    onOpenDeleteModal();
  };

  const handleDelete = () => {
    // dispatch(deleteNotificationAsync(deletingNotification.id))
    setDeletingNotification(null);
    onCloseDeleteModal();

    toast({
      title: "Notification deleted",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleView = (notification) => {
    setViewingNotification(notification);
    onOpenViewModal();
  };

  const resetForm = () => {
    setNewNotification({
      send_type: "notification",
      send_to: "",
      title: "",
      description: "",
    });
    setSelectedImageFile(null);
    setPreviewImageUrl(null);
    setSearchQuery("");
    dispatch(clearUsers());
    setSelectedRecipients([]);
    setError(null);
  };

  const showRecipientSearch =
    newNotification.send_to === "Selected candidate" ||
    newNotification.send_to === "Selected Employer";

  return (
    <div
      className="h-screen bg-adminbg contentAdmin p-4"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className=" ">
        <div className="w-full my-5 bg-white rounded-xl">
          <div className="flex justify-between">
            <p className="text-xl text-black font-semibold px-3 py-7">
              Notification Management
            </p>
            <div className="py-4 px-3 flex gap-3">
              <Button
                colorScheme="blue"
                fontSize={"medium"}
                alignContent={"center"}
                onClick={onOpenAddModal}
              >
                Add<i className="fa-solid fa-plus px-2"></i>
              </Button>

              {/* Add Notification Modal */}
              <Modal
                isOpen={isAddModalOpen}
                onClose={() => { resetForm(); onCloseAddModal(); }}
                size="4xl"
              >
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                    Add Notification
                  </ModalHeader>
                  <ModalCloseButton color="white" />
                  <ModalBody py={6}>
                    <VStack spacing={6}>
                      <Grid
                        templateColumns="repeat(2, 1fr)"
                        gap={4}
                        width="100%"
                      >
                        <GridItem colSpan={1}>
                          <FormControl isRequired>
                            <FormLabel>Send Type</FormLabel>
                            <Select
                              name="send_type"
                              value={newNotification.send_type}
                              onChange={handleInputChange}
                            >
                              <option value="notification">Notification</option>
                            </Select>
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={1}>
                          <FormControl isRequired>
                            <FormLabel>Send To</FormLabel>
                            <Select
                              name="send_to"
                              value={newNotification.send_to}
                              onChange={handleInputChange}
                            >
                              <option value="">Select recipient</option>
                              <option value="Selected candidate">
                                Selected candidate
                              </option>
                              <option value="Selected Employer">
                                Selected Employer
                              </option>
                              <option value="All candidate">
                                All candidate
                              </option>
                              <option value="All Employer">All Employer</option>
                            </Select>
                          </FormControl>
                        </GridItem>

                        {/* Recipient search section */}
                        {showRecipientSearch && (
                          <GridItem colSpan={2}>
                            <FormControl>
                              <FormLabel>
                                Search{" "}
                                {newNotification.send_to ===
                                  "Selected candidate"
                                  ? "Candidates"
                                  : "Employers"}
                              </FormLabel>
                              <InputGroup>
                                <InputLeftElement pointerEvents="none">
                                  <SearchIcon color="gray.300" />
                                </InputLeftElement>
                                <Input
                                  placeholder="Search by email, phone or name"
                                  value={searchQuery}
                                  onChange={handleSearch}
                                />
                              </InputGroup>

                              {/* Search results */}
                              {searchQuery.length > 2 && (
                                <Box
                                  mt={2}
                                  border="1px"
                                  borderColor="gray.200"
                                  borderRadius="md"
                                  maxH="200px"
                                  overflowY="auto"
                                >
                                  {searchResults?.length > 0 ? (
                                    searchResults.map((user) => (
                                      <Box
                                        key={user.uuid || user.id}
                                        p={2}
                                        _hover={{ bg: "gray.100" }}
                                        cursor="pointer"
                                        onClick={() => addRecipient(user)}
                                      >
                                        <Text fontWeight="medium">
                                          {user.name ||
                                            user.first_name +
                                            " " +
                                            user.last_name}
                                        </Text>
                                        <Text fontSize="sm" color="gray.600">
                                          {user.email}{" "}
                                          {user.phone && `| ${user.phone}`}
                                        </Text>
                                      </Box>
                                    ))
                                  ) : (
                                    <Box p={4} textAlign="center">
                                      <Text color="gray.500">
                                        No results found
                                      </Text>
                                    </Box>
                                  )}
                                </Box>
                              )}

                              {/* Selected recipients */}
                              {selectedRecipients.length > 0 && (
                                <Box mt={4}>
                                  <Text mb={2} fontWeight="medium">
                                    Selected Recipients:
                                  </Text>
                                  <Flex flexWrap="wrap" gap={2}>
                                    {selectedRecipients.map((recipient) => (
                                      <Tag
                                        key={recipient.uuid || recipient.id}
                                        size="md"
                                        borderRadius="full"
                                        variant="solid"
                                        colorScheme="blue"
                                      >
                                        <TagLabel>
                                          {recipient.name ||
                                            recipient.first_name +
                                            " " +
                                            recipient.last_name}
                                        </TagLabel>
                                        <TagCloseButton
                                          onClick={() =>
                                            removeRecipient(
                                              recipient.uuid || recipient.id
                                            )
                                          }
                                        />
                                      </Tag>
                                    ))}
                                  </Flex>
                                </Box>
                              )}
                            </FormControl>
                          </GridItem>
                        )}

                        <GridItem colSpan={2}>
                          <FormControl isRequired>
                            <FormLabel>Title</FormLabel>
                            <Input
                              name="title"
                              placeholder="Enter notification title"
                              value={newNotification.title}
                              onChange={handleInputChange}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl isRequired>
                            <FormLabel>Description</FormLabel>
                            <Textarea
                              name="description"
                              placeholder="Enter notification description"
                              value={newNotification.description}
                              onChange={handleInputChange}
                              rows={4}
                            />
                          </FormControl>
                        </GridItem>

                        {/* Image Upload Field */}
                        {newNotification.send_type === "notification" && (
                          <GridItem colSpan={2}>
                            <FormControl>
                              <FormLabel>Upload Image</FormLabel>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                pt={1}
                              />
                              {previewImageUrl && (
                                <Box mt={2}>
                                  <Text fontSize="sm" mb={1}>Preview:</Text>
                                  <Image
                                    src={previewImageUrl}
                                    alt="Preview"
                                    boxSize="150px"
                                    objectFit="cover"
                                    borderRadius="md"
                                  />
                                  <Button
                                    size="xs"
                                    colorScheme="red"
                                    mt={1}
                                    onClick={() => {
                                      setSelectedImageFile(null);
                                      setPreviewImageUrl(null);
                                    }}
                                  >
                                    Remove Image
                                  </Button>
                                </Box>
                              )}
                            </FormControl>
                          </GridItem>
                        )}
                      </Grid>
                      {error && <Text color="red.500">{error}</Text>}
                    </VStack>
                  </ModalBody>
                  <ModalFooter bg="gray.50" borderBottomRadius="md">
                    <Button colorScheme="blue" mr={3} onClick={handleSave}>
                      Save
                    </Button>
                    <Button
                      onClick={() => {
                        resetForm();
                        onCloseAddModal();
                      }}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>

              {/* Delete Confirmation Modal (Unchanged) */}
              <Modal isOpen={isDeleteModalOpen} onClose={onCloseDeleteModal}>
                <ModalOverlay />
                <ModalContent maxW="2xl" borderradius="lg" boxShadow="lg">
                  <ModalHeader
                    fontSize="lg"
                    fontWeight="bold"
                    textAlign="center"
                  >
                    Confirm Deletion
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Text fontSize="md" textAlign="center">
                      Are you sure you want to delete{" "}
                      <Text as="span" fontWeight="semibold" color="red.500">
                        "{deletingNotification?.title}"
                      </Text>
                      ?
                    </Text>
                  </ModalBody>
                  <ModalFooter display="flex" gap="2" justifyContent="center">
                    <Button colorScheme="red" w="40" onClick={handleDelete}>
                      Delete
                    </Button>
                    <Button
                      variant="outline"
                      colorScheme="red"
                      w="40"
                      onClick={onCloseDeleteModal}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>

              {/* View Notification Modal (Unchanged) */}
              <Modal
                isOpen={isViewModalOpen}
                onClose={onCloseViewModal}
                size="lg"
              >
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                    View Notification
                  </ModalHeader>
                  <ModalCloseButton color="white" />
                  <ModalBody py={6}>
                    {viewingNotification && (
                      <VStack spacing={4} align="start">
                        <Box width="100%">
                          <Text fontWeight="bold">Send Type:</Text>
                          <Text>{viewingNotification.send_type}</Text>
                        </Box>
                        <Box width="100%">
                          <Text fontWeight="bold">Send To:</Text>
                          <Text>{viewingNotification.send_to}</Text>
                        </Box>
                        <Box width="100%">
                          <Text fontWeight="bold">Title:</Text>
                          <Text>{viewingNotification.title}</Text>
                        </Box>
                        <Box width="100%">
                          <Text fontWeight="bold">Description:</Text>
                          <Text>{viewingNotification.description}</Text>
                        </Box>
                        {viewingNotification.send_type === "notification" &&
                          viewingNotification.image_url && (
                            <Box width="100%">
                              <Text fontWeight="bold">Image:</Text>
                              <Image
                                src={
                                  viewingNotification.image_url ||
                                  "/placeholder.svg"
                                }
                                alt="Notification image"
                                maxH="200px"
                                mt={2}
                                borderRadius="md"
                              />
                            </Box>
                          )}
                      </VStack>
                    )}
                  </ModalBody>
                  <ModalFooter bg="gray.50" borderBottomRadius="md">
                    <Button onClick={onCloseViewModal}>Close</Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </div>
          </div>
          <TableContainer>
            <Table size="sm">
              <Thead className="bg-gray-200">
                <Tr>
                  <Th className="w-1/12">ID</Th>
                  <Th className="w-1/6">Send Type</Th>
                  <Th className="w-1/6">Send To</Th>
                  <Th className="w-1/4">Title</Th>
                  <Th className="w-1/4">Description</Th>
                  <Th className="w-1/6">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading ? (
                  <Tr>
                    <Td colSpan="6" className="text-center py-4">Loading...</Td>
                  </Tr>
                ) : notifications?.length > 0 ? (
                  notifications.map((notification) => (
                    <Tr key={notification.id}>
                      <Td>{notification.id}</Td>
                      <Td>
                        <Text
                          px={2}
                          py={1}
                          borderRadius="md"
                          bg={
                            notification.send_type === "notification"
                              ? "blue.100"
                              : "green.100"
                          }
                          color={
                            notification.send_type === "notification"
                              ? "blue.700"
                              : "green.700"
                          }
                          display="inline-block"
                          fontSize="sm"
                        >
                          {notification.send_type}
                        </Text>
                      </Td>
                      <Td>{notification.send_to}</Td>
                      <Td>{notification.title}</Td>
                      <Td>
                        {notification.description?.length > 30
                          ? `${notification.description?.substring(0, 30)}...`
                          : notification.description}
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="View Notification" placement="top">
                            <IconButton
                              icon={<ViewIcon />}
                              colorScheme="teal"
                              size="sm"
                              onClick={() => handleView(notification)}
                              aria-label="View notification"
                            />
                          </Tooltip>
                          <IconButton
                            icon={<DeleteIcon />}
                            colorScheme="red"
                            size="sm"
                            onClick={() => openDeleteModal(notification)}
                            aria-label="Delete notification"
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan="6" className="text-center py-4 text-gray-500">
                      No notifications available
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {/* Pagination using API data */}
      <GridItem colSpan={2} display="flex" justifyContent="center">
        <div className="flex w-full justify-evenly items-center mt-4">
          <button
            className={`bg-gray-300 text-black px-4 py-2 rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
              }`}
            onClick={handlePrevPage}
            disabled={currentPage === 1 || isLoading}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className={`bg-blue-500 text-white px-4 py-2 rounded ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""
              }`}
            onClick={handleNextPage}
            disabled={currentPage === totalPages || isLoading}
          >
            Next
          </button>
        </div>
      </GridItem>
    </div>
  );
}

export default NotificationList;